import { useScript } from '@hooks/use-script';
import { ReactElement, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Logger } from '@utils/logger';
import { getTMXIDWJS, getTMXOrgId, getTMXPageId, getTMXProfilingDomain, getTMXSessionId } from '@redux/selectors/verification-configuration-selectors';

declare global {
    interface Window {
        tmx_profiling_complete: any;
        idwtmx: any;
    }
}

type TMXComponentProps = {
    onTMXProfilingComplete: (sessionId: string) => void;
    onTMXLoadError: (error: string) => void;
    children: ReactElement;
};

export const TMXComponent = (props: TMXComponentProps) => {
    const { onTMXProfilingComplete, onTMXLoadError, children } = props;

	const tmxProfilingDomain = useSelector(getTMXProfilingDomain);
	const tmxOrgID = useSelector(getTMXOrgId);
	const tmxPageID = useSelector(getTMXPageId);
	const tmxSessionID = useSelector(getTMXSessionId);
	const tmxIDWJS = useSelector(getTMXIDWJS);

	const [loaded, error, loading] = useScript(tmxIDWJS);
	const timeoutRef = useRef(null);

	const onTmxProfilingComplete = (sessionId: string) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		onTMXProfilingComplete(sessionId);
	};

	const onTmxLoadError = (error: string) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		onTMXLoadError(error);
	};

	window.tmx_profiling_complete = onTmxProfilingComplete;

	useEffect(() => {
		if (loading) {
			Logger.info('[TMX] Loading the TMX script');
			//set a 10 second timeout
			timeoutRef.current = setTimeout(() => {
				onTmxLoadError('Script has taken more than 10 seconds to run. Enabling Verify button.');
			}, 10000);
		}
	}, [loading]);

	useEffect(() => {
		if (loaded && !error && window.idwtmx) {
			try {
				window.idwtmx.profile(tmxProfilingDomain, tmxOrgID, tmxSessionID, tmxPageID);
				Logger.info('[TMX] Loaded the TMX script');
			} catch (error) {
				Logger.error(`[TMX] TMX Profile failed with error: `, error);
				onTmxLoadError(`[TMX] TMX Profile failed with error: ${error}`);
			}
		} else if (loaded && error) {
			onTmxLoadError("[TMX] useScript failed to load TMX");
		} else if (!window.idwtmx) {
			Logger.info(`[TMX] the window doesn't have the idwtmx script yet`);
		}
	}, [loaded, error]);

	return children;
};
